import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Subject, Subscription } from 'rxjs';
import { Translatable } from './translatable';
import { TranslatableDirective } from './translatable.directive';
import { TranslationDialogComponent } from './translation-dialog/translation-dialog.component';

export type TranslatorInputType = 'text' | 'textarea';

export interface TranslationDialogData {
  value: Translatable;
  type?: TranslatorInputType;
  title?: string;
}

@Component({
  selector: 'atm-translator',
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class TranslatorComponent implements OnDestroy {
  private dialogRef?: MatDialogRef<TranslationDialogComponent, Translatable>;
  private inputStateChanges = Subscription.EMPTY;

  @Input()
  type: TranslatorInputType = 'text';

  @Input()
  title = '';

  @Input()
  get disabled(): boolean {
    return this.isDisabled === undefined && this.translatableInput
      ? this.translatableInput.disabled
      : !!this.isDisabled;
  }
  set disabled(value: boolean) {
    const newValue = coerceBooleanProperty(value);

    if (newValue !== this.isDisabled) {
      this.isDisabled = newValue;
      this.stateChanges.next(undefined);
    }
  }
  private isDisabled = false;

  @Input()
  get isOpened(): boolean {
    return this.isTranslatorOpened;
  }
  set isOpened(value: boolean) {
    coerceBooleanProperty(value) ? this.open() : this.close();
  }
  private isTranslatorOpened = false;

  readonly stateChanges = new Subject<void>();
  @Output() readonly opened = new EventEmitter<void>();
  @Output() readonly closed = new EventEmitter<Translatable | void>();

  get input(): TranslatableDirective | undefined {
    return this.translatableInput;
  }
  private translatableInput?: TranslatableDirective;

  constructor(
    private viewContainerRef: ViewContainerRef,
    public dialog: MatDialog
  ) {}

  ngOnDestroy(): void {
    this.close();
    this.stateChanges.complete();
    this.inputStateChanges.unsubscribe();
  }

  open(): void {
    if (this.isTranslatorOpened || this.disabled) {
      return;
    }
    if (!this.translatableInput) {
      throw Error('Attempted to open an translator with no associated input.');
    }
    this.openDialog();
    this.isTranslatorOpened = true;
    this.opened.emit();
  }

  close(value?: Translatable): void {
    if (!this.isTranslatorOpened) {
      return;
    }
    if (this.dialogRef) {
      this.dialogRef.close();
      this.dialogRef = undefined;
    }
    this.isTranslatorOpened = false;
    if (value) {
      this.closed.emit(value);
    } else {
      this.closed.emit();
    }
  }

  registerInput(input: TranslatableDirective): void {
    if (!input) {
      throw new Error('Cannot use translator without an input');
    }
    this.inputStateChanges.unsubscribe();
    this.translatableInput = input;
    this.inputStateChanges = input.stateChanges.subscribe(() =>
      this.stateChanges.next(undefined)
    );
  }

  private openDialog(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
    if (!this.translatableInput) {
      throw new Error('Missing input[atmTranslatable]');
    }

    this.dialogRef = this.dialog.open(TranslationDialogComponent, {
      viewContainerRef: this.viewContainerRef,
      panelClass: 'atm-translatable-panel',
      autoFocus: false,
      restoreFocus: false,
      width: '680px',
      data: {
        value: this.translatableInput.value,
        type: this.type,
        title: this.title,
      },
    });

    this.dialogRef.afterClosed().subscribe((res) => this.close(res));
  }
}
